import type { Map } from "mapbox-gl"
import { endangeredSpeciesLayerDefinitions } from "@/components/map/environmentLayers"
import { PLANNING_POLICY_COLORS } from "@/components/map/planningLayers"

export async function loadHatchPatterns(map: Map, isCurrentStyle: () => boolean): Promise<void> {
	const patternDefs = [
		{ name: "planning-hatch-tod", color: PLANNING_POLICY_COLORS.tod },
		{ name: "planning-hatch-ppu", color: PLANNING_POLICY_COLORS.ppu },
		{ name: "planning-hatch-corridor", color: PLANNING_POLICY_COLORS.corridor },
		{ name: "planning-hatch-strategic", color: PLANNING_POLICY_COLORS.strategic },
		...endangeredSpeciesLayerDefinitions.map(definition => ({
			name: `${definition.id}-plant-hatch`,
			color: definition.fillColor,
		})),
	]

	// draw a 45deg hatch to a canvas, export as PNG data URL so mapbox-gl treats it as an image pattern
	const createPatternDataUrl = (color: string): string => {
		const size = 16
		const canvas = document.createElement("canvas")
		canvas.width = size
		canvas.height = size
		const ctx = canvas.getContext("2d")
		if (!ctx) return ""
		ctx.strokeStyle = color
		ctx.lineWidth = 2
		ctx.lineCap = "square"

		// replicate the provided diagonal motif
		ctx.beginPath()
		ctx.moveTo(size, 0)
		ctx.lineTo(0, size)
		ctx.stroke()

		// small corners to match sample
		ctx.beginPath()
		ctx.moveTo(0, 0)
		ctx.lineTo(0, 1)
		ctx.lineTo(1, 0)
		ctx.fillStyle = color
		ctx.fill()

		ctx.beginPath()
		ctx.moveTo(size, size)
		ctx.lineTo(size - 1, size)
		ctx.lineTo(size, size - 1)
		ctx.fill()

		return canvas.toDataURL("image/png")
	}

	await Promise.all(
		patternDefs.map(
			p =>
				new Promise<void>((resolve, reject) => {
					const url = createPatternDataUrl(p.color)
					map.loadImage(url, (err, img) => {
						if (err || !img) {
							reject(err)
							return
						}
						if (!isCurrentStyle()) {
							resolve()
							return
						}
						if (!map.hasImage(p.name)) {
							map.addImage(p.name, img, { pixelRatio: 2 })
						}
						resolve()
					})
				})
		)
	)
}
